import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

import {
  QrCode,
  Search,
  UserPlus,
  Clock,
  Users,
  CheckCircle,
  Camera,
  Scan,
  LogOut,
  TrendingUp,
  Timer,
} from "lucide-react";

interface Client {
  id: number;
  name: string;
  email: string;
  phone: string;
  status: string;
  subscriptionType: string;
  avatar: string;
  lastVisit: string;
  totalVisits: number;
  hoursRemaining?: number;
}

interface CheckInEntry {
  id: number;
  clientId: number;
  clientName: string;
  avatar: string;
  checkInTime: string;
  checkOutTime?: string;
  method: "search" | "qr" | "manual";
  subscriptionType: string;
  status: string;
  hoursUsed?: number;
  cost?: number;
}

interface UsageEntry {
  id: number;
  clientName: string;
  date: string;
  checkIn: string;
  checkOut: string;
  hoursUsed: number;
  packageType: string;
  cost: number;
}

// interface DailyStats {
//   date: string;
//   totalHours: number;
//   totalClients: number;
//   revenue: number;
// }

export function CheckIn() {
  const [clients] = useState<Client[]>([
    {
      id: 1,
      name: "John Doe",
      email: "john@example.com",
      phone: "+63 912 345 6789",
      status: "Active",
      subscriptionType: "Monthly Premium",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      lastVisit: "2024-06-17",
      totalVisits: 45,
    },
    {
      id: 2,
      name: "Sarah Wilson",
      email: "sarah@example.com",
      phone: "+63 917 234 5678",
      status: "Active",
      subscriptionType: "30-hour Package",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b605?w=150&h=150&fit=crop&crop=face",
      lastVisit: "2024-06-16",
      totalVisits: 28,
      hoursRemaining: 15,
    },
    {
      id: 3,
      name: "Mike Johnson",
      email: "mike@example.com",
      phone: "+63 920 345 6789",
      status: "Active",
      subscriptionType: "Daily Pass",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      lastVisit: "2024-06-15",
      totalVisits: 12,
    },
    {
      id: 4,
      name: "Emily Chen",
      email: "emily@example.com",
      phone: "+63 918 234 5678",
      status: "Active",
      subscriptionType: "Monthly Regular",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      lastVisit: "2024-06-17",
      totalVisits: 33,
    },
    {
      id: 5,
      name: "David Rodriguez",
      email: "david@example.com",
      phone: "+63 915 234 5678",
      status: "Expiring",
      subscriptionType: "10-hour Package",
      avatar:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      lastVisit: "2024-06-14",
      totalVisits: 8,
      hoursRemaining: 3,
    },
  ]);

  const [todayCheckIns, setTodayCheckIns] = useState<CheckInEntry[]>([
    {
      id: 1,
      clientId: 1,
      clientName: "John Doe",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      checkInTime: "08:30 AM",
      method: "qr",
      subscriptionType: "Monthly Premium",
      status: "Active",
    },
    {
      id: 2,
      clientId: 4,
      clientName: "Emily Chen",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      checkInTime: "09:15 AM",
      method: "search",
      subscriptionType: "Monthly Regular",
      status: "Active",
    },
  ]);

  const [
    usageEntries,
    // setUsageEntries
  ] = useState<UsageEntry[]>([
    {
      id: 1,
      clientName: "John Doe",
      date: "2024-06-18",
      checkIn: "09:00",
      checkOut: "17:00",
      hoursUsed: 8,
      packageType: "Monthly Premium",
      cost: 0,
    },
    {
      id: 2,
      clientName: "Sarah Wilson",
      date: "2024-06-18",
      checkIn: "10:30",
      checkOut: "15:30",
      hoursUsed: 5,
      packageType: "30-hour package",
      cost: 0,
    },
    {
      id: 3,
      clientName: "Mike Johnson",
      date: "2024-06-17",
      checkIn: "08:00",
      checkOut: "12:00",
      hoursUsed: 4,
      packageType: "Hourly Rate",
      cost: 3300,
    },
    {
      id: 4,
      clientName: "Emma Brown",
      date: "2024-06-17",
      checkIn: "14:00",
      checkOut: "18:00",
      hoursUsed: 4,
      packageType: "10-hour package",
      cost: 0,
    },
    {
      id: 5,
      clientName: "Alex Turner",
      date: "2024-06-16",
      checkIn: "11:00",
      checkOut: "16:00",
      hoursUsed: 5,
      packageType: "Daily Pass",
      cost: 1925,
    },
  ]);

  // const [newUsageEntry, setNewUsageEntry] = useState({
  //   clientName: "",
  //   checkIn: "",
  //   checkOut: "",
  //   packageType: "",
  // });

  // const packageTypes = [
  //   "Monthly Premium",
  //   "Monthly Regular",
  //   "30-hour package",
  //   "10-hour package",
  //   "Daily Pass",
  //   "Hourly Rate",
  // ];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showCheckInConfirm, setShowCheckInConfirm] = useState(false);
  const [showCheckOutDialog, setShowCheckOutDialog] = useState(false);
  const [checkingOutEntry, setCheckingOutEntry] = useState<CheckInEntry | null>(
    null
  );

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.id.toString().includes(searchTerm) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setShowCheckInConfirm(true);
  };

  const handleCheckIn = (method: "search" | "qr" | "manual" = "search") => {
    if (!selectedClient) return;

    const now = new Date();
    const timeString = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const newCheckIn: CheckInEntry = {
      id: todayCheckIns.length + 1,
      clientId: selectedClient.id,
      clientName: selectedClient.name,
      avatar: selectedClient.avatar,
      checkInTime: timeString,
      method: method,
      subscriptionType: selectedClient.subscriptionType,
      status: selectedClient.status,
    };

    setTodayCheckIns([newCheckIn, ...todayCheckIns]);
    setSelectedClient(null);
    setShowCheckInConfirm(false);
    setSearchTerm("");
  };

  const handleCheckOut = (entry: CheckInEntry) => {
    setCheckingOutEntry(entry);
    setShowCheckOutDialog(true);
  };

  const confirmCheckOut = () => {
    if (!checkingOutEntry) return;

    const now = new Date();
    const timeString = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    // Calculate hours used (simplified calculation)
    const checkInHour = parseInt(checkingOutEntry.checkInTime.split(":")[0]);
    const currentHour = now.getHours();
    const hoursUsed = Math.max(
      1,
      currentHour -
        (checkInHour + (checkingOutEntry.checkInTime.includes("PM") ? 12 : 0))
    );

    const updatedEntry = {
      ...checkingOutEntry,
      checkOutTime: timeString,
      hoursUsed: hoursUsed,
      cost: calculateCost(hoursUsed, checkingOutEntry.subscriptionType),
    };

    setTodayCheckIns((entries) =>
      entries.map((entry) =>
        entry.id === checkingOutEntry.id ? updatedEntry : entry
      )
    );

    setCheckingOutEntry(null);
    setShowCheckOutDialog(false);
  };

  const calculateCost = (hours: number, packageType: string): number => {
    switch (packageType) {
      case "Hourly Rate":
        return hours * 825;
      case "Daily Pass":
        return 1925;
      case "Monthly Premium":
      case "Monthly Regular":
      case "30-hour Package":
      case "10-hour Package":
        return 0; // Already paid
      default:
        return 0;
    }
  };

  // const calculateHours = (checkIn: string, checkOut: string): number => {
  //   if (!checkIn || !checkOut) return 0;
  //   const inTime = new Date(`2024-01-01T${checkIn}`);
  //   const outTime = new Date(`2024-01-01T${checkOut}`);
  //   return Math.max(
  //     0,
  //     (outTime.getTime() - inTime.getTime()) / (1000 * 60 * 60)
  //   );
  // };

  // const handleAddUsageEntry = () => {
  //   if (
  //     !newUsageEntry.clientName ||
  //     !newUsageEntry.checkIn ||
  //     !newUsageEntry.checkOut ||
  //     !newUsageEntry.packageType
  //   )
  //     return;

  //   const hoursUsed = calculateHours(
  //     newUsageEntry.checkIn,
  //     newUsageEntry.checkOut
  //   );
  //   const cost = calculateCost(hoursUsed, newUsageEntry.packageType);

  //   const entry: UsageEntry = {
  //     id: Math.max(...usageEntries.map((e) => e.id)) + 1,
  //     ...newUsageEntry,
  //     date: new Date().toISOString().split("T")[0],
  //     hoursUsed,
  //     cost,
  //   };

  //   setUsageEntries([...usageEntries, entry]);
  //   setNewUsageEntry({
  //     clientName: "",
  //     checkIn: "",
  //     checkOut: "",
  //     packageType: "",
  //   });
  // };

  const simulateQRScan = () => {
    // Simulate QR scan by randomly selecting a client
    const randomClient = clients[Math.floor(Math.random() * clients.length)];
    setSelectedClient(randomClient);
    setShowQRScanner(false);
    setShowCheckInConfirm(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Expiring":
        return "bg-yellow-100 text-yellow-800";
      case "Expired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // const getMethodIcon = (method: string) => {
  //   switch (method) {
  //     case "qr":
  //       return <QrCode className="h-4 w-4" />;
  //     case "search":
  //       return <Search className="h-4 w-4" />;
  //     case "manual":
  //       return <UserPlus className="h-4 w-4" />;
  //     default:
  //       return <Clock className="h-4 w-4" />;
  //   }
  // };

  // const getMethodLabel = (method: string) => {
  //   switch (method) {
  //     case "qr":
  //       return "QR Scan";
  //     case "search":
  //       return "Search";
  //     case "manual":
  //       return "Manual";
  //     default:
  //       return "Unknown";
  //   }
  // };

  const getPackageTypeColor = (type: string) => {
    switch (type) {
      case "Monthly Premium":
        return "bg-purple-100 text-purple-800";
      case "Monthly Regular":
        return "bg-blue-100 text-blue-800";
      case "Hourly Rate":
        return "bg-green-100 text-green-800";
      case "Daily Pass":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const todayDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Calculate today's usage stats
  const totalHoursToday = usageEntries
    .filter((entry) => entry.date === new Date().toISOString().split("T")[0])
    .reduce((sum, entry) => sum + entry.hoursUsed, 0);

  const totalRevenueToday = usageEntries
    .filter((entry) => entry.date === new Date().toISOString().split("T")[0])
    .reduce((sum, entry) => sum + entry.cost, 0);

  // const totalClientsToday = new Set(
  //   usageEntries
  //     .filter((entry) => entry.date === new Date().toISOString().split("T")[0])
  //     .map((entry) => entry.clientName)
  // ).size;

  const activeCheckIns = todayCheckIns.filter((entry) => !entry.checkOutTime);

  // Calculate daily stats
  // const dailyStats: DailyStats[] = usageEntries
  //   .reduce((acc: DailyStats[], entry) => {
  //     const existingDay = acc.find((day) => day.date === entry.date);
  //     if (existingDay) {
  //       existingDay.totalHours += entry.hoursUsed;
  //       existingDay.totalClients += 1;
  //       existingDay.revenue += entry.cost;
  //     } else {
  //       acc.push({
  //         date: entry.date,
  //         totalHours: entry.hoursUsed,
  //         totalClients: 1,
  //         revenue: entry.cost,
  //       });
  //     }
  //     return acc;
  //   }, [])
  //   .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1>Tap In/Out Center</h1>
            <p className="text-muted-foreground">
              Register client entries and manage workspace usage
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Today</p>
            <p className="font-medium">{todayDate}</p>
          </div>
        </div>
      </div>

      {/* Register Entry - Top Priority Section */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            Register Entry
          </CardTitle>
          <CardDescription>
            Search for a client or scan their QR code to register entry
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client-search">Search Client</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="client-search"
                  placeholder="Search by name, ID, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Dialog open={showQRScanner} onOpenChange={setShowQRScanner}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0">
                    <QrCode className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>QR Code Scanner</DialogTitle>
                    <DialogDescription>
                      Point the camera at the client's QR code to register their
                      entry
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col items-center space-y-4 py-6">
                    <div className="w-48 h-48 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center bg-muted/10">
                      <div className="text-center space-y-2">
                        <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Camera View
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Simulate QR scan
                        </p>
                      </div>
                    </div>
                    <Button onClick={simulateQRScan} className="w-full">
                      <Scan className="mr-2 h-4 w-4" />
                      Simulate QR Scan
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search Results */}
          {searchTerm && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleClientSelect(client)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={client.avatar} alt={client.name} />
                      <AvatarFallback>
                        {client.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{client.name}</p>
                        <Badge
                          className={getStatusColor(client.status)}
                          variant="secondary"
                        >
                          {client.status}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground truncate">
                          ID: {client.id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {client.subscriptionType}
                        </p>
                      </div>
                      {client.hoursRemaining && (
                        <p className="text-xs text-blue-600">
                          {client.hoursRemaining} hours remaining
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No clients found matching "{searchTerm}"
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Today's Entries</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{todayCheckIns.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Active Sessions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{activeCheckIns.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Hours Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{totalHoursToday}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Revenue Today</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              ₱{totalRevenueToday.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <CardTitle>Active Client Sessions</CardTitle>
          <CardDescription>
            Clients currently using the workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeCheckIns.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Entry Time</TableHead>
                  <TableHead>Package Type</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeCheckIns.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage
                            src={entry.avatar}
                            alt={entry.clientName}
                          />
                          <AvatarFallback>
                            {entry.clientName
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{entry.clientName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{entry.checkInTime}</TableCell>
                    <TableCell>
                      <Badge
                        className={getPackageTypeColor(entry.subscriptionType)}
                        variant="secondary"
                      >
                        {entry.subscriptionType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Timer className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Active</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCheckOut(entry)}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Tap Out
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No active sessions</p>
              <p className="text-sm">All clients have tapped out</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Usage Logging */}

      {/* Usage History */}
      <Card>
        <CardHeader>
          <CardTitle>Usage History</CardTitle>
          <CardDescription>Complete workspace usage records</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Entry Time</TableHead>
                <TableHead>Exit Time</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usageEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.clientName}</TableCell>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.checkIn}</TableCell>
                  <TableCell>{entry.checkOut}</TableCell>
                  <TableCell>{entry.hoursUsed.toFixed(1)}</TableCell>
                  <TableCell>
                    <Badge
                      className={getPackageTypeColor(entry.packageType)}
                      variant="secondary"
                    >
                      {entry.packageType}
                    </Badge>
                  </TableCell>
                  <TableCell>₱{entry.cost.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Check-in Confirmation Dialog */}
      <Dialog open={showCheckInConfirm} onOpenChange={setShowCheckInConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Entry</DialogTitle>
            <DialogDescription>
              Register entry for this client to the coworking space
            </DialogDescription>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-4 rounded-lg border">
                <Avatar className="h-12 w-12">
                  <AvatarImage
                    src={selectedClient.avatar}
                    alt={selectedClient.name}
                  />
                  <AvatarFallback>
                    {selectedClient.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{selectedClient.name}</h4>
                    <Badge
                      className={getStatusColor(selectedClient.status)}
                      variant="secondary"
                    >
                      {selectedClient.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    ID: {selectedClient.id}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedClient.subscriptionType}
                  </p>
                  {selectedClient.hoursRemaining && (
                    <p className="text-sm text-blue-600">
                      {selectedClient.hoursRemaining} hours remaining
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Last Visit
                  </Label>
                  <p className="text-sm">{selectedClient.lastVisit}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Total Visits
                  </Label>
                  <p className="text-sm">{selectedClient.totalVisits}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleCheckIn("search")}
                  className="flex-1"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Tap In
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCheckInConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-out Confirmation Dialog */}
      <Dialog open={showCheckOutDialog} onOpenChange={setShowCheckOutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Exit</DialogTitle>
            <DialogDescription>
              Complete the session for this client
            </DialogDescription>
          </DialogHeader>

          {checkingOutEntry && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-4 rounded-lg border">
                <Avatar className="h-12 w-12">
                  <AvatarImage
                    src={checkingOutEntry.avatar}
                    alt={checkingOutEntry.clientName}
                  />
                  <AvatarFallback>
                    {checkingOutEntry.clientName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h4 className="font-medium">{checkingOutEntry.clientName}</h4>
                  <p className="text-sm text-muted-foreground">
                    Tapped in at {checkingOutEntry.checkInTime}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {checkingOutEntry.subscriptionType}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={confirmCheckOut} className="flex-1">
                  <LogOut className="mr-2 h-4 w-4" />
                  Tap Out
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCheckOutDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
